import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";

// Conversa do escritório com o time do TUTO.
//
// Fica no canto inferior direito, fechada, e só chama atenção quando há algo
// para ler. A ligação é por WebSocket para a resposta aparecer enquanto a
// pessoa ainda está na tela — é esse o ponto do recurso: falar com o advogado
// no momento em que ele está usando o sistema.
//
// Quando o socket não sobe (rede corporativa que bloqueia upgrade, por
// exemplo), o histórico continua chegando por HTTP: a conversa fica mais lenta,
// não indisponível.

export function enderecoDoSocket(local = window.location) {
  const protocolo = local.protocol === "https:" ? "wss:" : "ws:";
  // Em desenvolvimento o front roda em 3401 e a API em 8401.
  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (base) return `${base.replace(/^http/, "ws")}/api/suporte/ws`;
  return `${protocolo}//${local.host}/api/suporte/ws`;
}

// O e-mail de aviso traz ?chat=aberto: quem clica cai na conta com a conversa
// já na tela, sem ter de procurar a bolha no canto.
export function pediuAbrirChat(busca) {
  return new URLSearchParams(busca || "").get("chat") === "aberto";
}

export function ordenar(mensagens) {
  return [...mensagens].sort((a, b) => String(a.criada_em).localeCompare(String(b.criada_em)));
}

export function juntar(atuais, novas) {
  // O socket reenvia o que chegou depois da conexão; o histórico inicial pode
  // repetir. O id decide.
  const porId = new Map(atuais.map((m) => [m.mensagem_id, m]));
  novas.forEach((m) => porId.set(m.mensagem_id, m));
  return ordenar([...porId.values()]);
}

export function formatarHora(iso) {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "";
  return data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export default function ChatSuporte({ api, fetcher = fetch }) {
  const [aberto, setAberto] = useState(() => pediuAbrirChat(window.location.search));
  const [mensagens, setMensagens] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [texto, setTexto] = useState("");
  const [conectado, setConectado] = useState(false);
  const socket = useRef(null);
  const fim = useRef(null);
  const reconexao = useRef(null);

  const receber = useCallback((dado) => {
    if (dado.tipo === "historico") {
      setMensagens(ordenar(dado.mensagens || []));
      setNaoLidas(dado.nao_lidas || 0);
      return;
    }
    if (dado.tipo === "mensagem" && dado.mensagem) {
      setMensagens((atuais) => juntar(atuais, [dado.mensagem]));
      if (dado.mensagem.autor === "admin") setNaoLidas((n) => n + 1);
    }
  }, []);

  useEffect(() => {
    let vivo = true;
    const conectar = () => {
      if (!vivo) return;
      let ws;
      try {
        ws = new WebSocket(enderecoDoSocket());
      } catch {
        return;
      }
      socket.current = ws;
      ws.onopen = () => setConectado(true);
      ws.onmessage = (evento) => {
        try {
          receber(JSON.parse(evento.data));
        } catch {
          // Quadro inválido não derruba a conversa.
        }
      };
      ws.onclose = () => {
        setConectado(false);
        // Reconecta sem insistir: quedas de rede são comuns em escritório.
        if (vivo) reconexao.current = setTimeout(conectar, 5000);
      };
      ws.onerror = () => ws.close();
    };
    conectar();
    return () => {
      vivo = false;
      clearTimeout(reconexao.current);
      socket.current?.close();
    };
  }, [receber]);

  // Sem socket, busca o histórico por HTTP para a conversa não sumir.
  useEffect(() => {
    if (conectado || !api) return undefined;
    let ativo = true;
    const buscar = () => {
      Promise.resolve(fetcher(`${api}/suporte/conversa`, { credentials: "include" }))
        .then((r) => (r.ok ? r.json() : null))
        .then((dados) => {
          if (!ativo || !dados) return;
          setMensagens(ordenar(dados.mensagens || []));
          setNaoLidas(dados.nao_lidas || 0);
        })
        .catch(() => {});
    };
    buscar();
    const timer = setInterval(buscar, 15000);
    return () => { ativo = false; clearInterval(timer); };
  }, [conectado, api, fetcher]);

  useEffect(() => {
    if (aberto) fim.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [mensagens, aberto]);

  // Chegou pelo link do e-mail: a conversa já está na tela, então as mensagens
  // contam como lidas. E o parâmetro sai do endereço, para recarregar a página
  // não reabrir sozinho mais tarde.
  useEffect(() => {
    if (!pediuAbrirChat(window.location.search)) return;
    setNaoLidas(0);
    if (api) {
      Promise.resolve(fetcher(`${api}/suporte/lidas`, { method: "POST", credentials: "include" }))
        .catch(() => {});
    }
    const params = new URLSearchParams(window.location.search);
    params.delete("chat");
    const resto = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (resto ? `?${resto}` : ""));
  }, [api, fetcher]);

  const abrir = () => {
    setAberto(true);
    setNaoLidas(0);
    if (socket.current?.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify({ tipo: "lidas" }));
    } else if (api) {
      Promise.resolve(fetcher(`${api}/suporte/lidas`, { method: "POST", credentials: "include" })).catch(() => {});
    }
  };

  const enviar = (evento) => {
    evento.preventDefault();
    const conteudo = texto.trim();
    if (!conteudo) return;
    setTexto("");
    if (socket.current?.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify({ tipo: "mensagem", texto: conteudo }));
      return;
    }
    Promise.resolve(fetcher(`${api}/suporte/mensagem`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ texto: conteudo }),
    }))
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => m && setMensagens((atuais) => juntar(atuais, [m])))
      .catch(() => {});
  };

  return (
    // Acima do botão do WhatsApp, que já ocupa o canto inferior direito.
    <div className="fixed bottom-24 right-6 z-50 flex flex-col items-end gap-3" data-testid="chat-suporte">
      {/* O aviso de mensagem nova aparece com o chat fechado — inclusive no
          primeiro acesso depois de o TUTO ter escrito. */}
      {!aberto && naoLidas > 0 && (
        <button
          type="button"
          onClick={abrir}
          data-testid="chat-aviso"
          className="max-w-xs bg-white border border-[#D1DEEE] rounded-lg shadow-lg px-4 py-3 text-left hover:border-[#003882]"
        >
          <p className="text-[#001533] font-semibold text-sm">Mensagem do TUTO</p>
          <p className="text-[#335480] text-sm mt-0.5 line-clamp-2">
            {mensagens.filter((m) => m.autor === "admin").at(-1)?.texto || "Você tem uma mensagem nova."}
          </p>
        </button>
      )}

      {aberto && (
        <section
          data-testid="chat-painel"
          aria-label="Conversa com o TUTO"
          className="w-[min(22rem,calc(100vw-3rem))] h-[26rem] bg-white border border-[#D1DEEE] rounded-lg shadow-xl flex flex-col overflow-hidden"
        >
          <header className="flex items-center justify-between px-4 py-3 border-b border-[#D1DEEE] bg-[#F4F7FB]">
            <div>
              <p className="font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>TUTO</p>
              <p className="text-[#6685AD] text-xs" data-testid="chat-estado">
                {conectado ? "conversa ao vivo" : "reconectando..."}
              </p>
            </div>
            <button type="button" onClick={() => setAberto(false)} aria-label="Fechar conversa" data-testid="chat-fechar">
              <X size={18} className="text-[#335480]" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {mensagens.length === 0 && (
              <p className="text-[#6685AD] text-sm text-center mt-8" data-testid="chat-vazio">
                Nenhuma mensagem ainda. Escreva se precisar de alguma coisa.
              </p>
            )}
            {mensagens.map((m) => (
              <div key={m.mensagem_id} className={m.autor === "usuario" ? "flex justify-end" : ""}>
                <div
                  data-testid={`chat-msg-${m.autor}`}
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                    m.autor === "usuario"
                      ? "bg-[#003882] text-white"
                      : "bg-[#F4F7FB] text-[#001533] border border-[#D1DEEE]"
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{m.texto}</p>
                  <p className={`text-[11px] mt-1 ${m.autor === "usuario" ? "text-[#c8d8ef]" : "text-[#6685AD]"}`}>
                    {formatarHora(m.criada_em)}
                  </p>
                </div>
              </div>
            ))}
            <div ref={fim} />
          </div>

          <form onSubmit={enviar} className="border-t border-[#D1DEEE] p-2 flex items-center gap-2">
            <label htmlFor="chat-texto" className="sr-only">Sua mensagem</label>
            <input
              id="chat-texto"
              data-testid="chat-texto"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Escreva sua mensagem"
              maxLength={2000}
              className="flex-1 px-3 py-2 text-[#001533] border border-[#D1DEEE] rounded-md focus:outline-none focus:border-[#003882]"
            />
            <button
              type="submit"
              data-testid="chat-enviar"
              aria-label="Enviar mensagem"
              disabled={!texto.trim()}
              className="bg-[#003882] hover:bg-[#00285e] disabled:opacity-40 text-white rounded-md p-2"
            >
              <Send size={16} strokeWidth={2} />
            </button>
          </form>
        </section>
      )}

      {!aberto && (
        <button
          type="button"
          onClick={abrir}
          data-testid="chat-bolha"
          aria-label={naoLidas > 0 ? `Conversa com o TUTO, ${naoLidas} não lidas` : "Conversa com o TUTO"}
          className="relative bg-[#003882] hover:bg-[#00285e] text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg"
        >
          <MessageCircle size={24} strokeWidth={1.75} />
          {naoLidas > 0 && (
            <span
              data-testid="chat-badge"
              className="absolute -top-1 -right-1 bg-[#E8B04B] text-[#001533] text-xs font-bold rounded-full min-w-[1.25rem] h-5 px-1 flex items-center justify-center"
            >
              {naoLidas > 9 ? "9+" : naoLidas}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
